/**
 * How one applicant lines up with what one vacancy asks for — "Encaje".
 *
 * Extracted from the admin's `ApplicantsPanel`, where the team reads it, so the
 * employer's dashboard on the public site can show the same reading of the same
 * two rows. Two copies of this would disagree within a month about what
 * "cumple" means, and the employer and the recruiter would be arguing over two
 * different screens.
 *
 * ── There is no score ────────────────────────────────────────────────
 *
 * Deliberately, and the reason has not changed since the panel said it: the
 * vacancy names a handful of sustainability areas and the candidate claims some
 * at a level they chose themselves; the overlap is a set intersection, printed
 * as the levels claimed. A single percentage would imply a precision that
 * self-declared questionnaire answers do not carry. Everything here is a list
 * of named facts — this area at this level, this credential held, that one
 * missing, that one never asked — and the only order imposed on applicants is
 * a chain of tie-breaks, spelled out in {@link compareFit}, not a number.
 *
 * ── Four answers, not two ────────────────────────────────────────────
 *
 * A credential is held, inferred from what they wrote, missing (asked, and not
 * there), or unknown (never asked) — see `requirements.ts`, and README, "The one
 * that mattered: skills". An area is claimed, not claimed, or unknown when the
 * candidate never answered the areas question. Only a *missing* required
 * credential is a verdict, and it is the vacancy's own, not ours.
 *
 * Import-free apart from its siblings, because the site bundles it; see
 * `tests/shared-config-imports.test.mjs`.
 */

import { AREA_LABELS, LEVEL_LABELS, LEVEL_RANK, canonicalAreaKey, isAreaLevel, isInflated, readAreas } from "./areas";
import type { AreaLevel, TalentArea } from "./areas";
import { relevantExperienceFloor } from "./experience";
import { candidateCredentials, classifyRequirements, vacancyRequirements } from "./requirements";
import type { RequirementVerdict, VacancyRequirements } from "./requirements";

/**
 * A catch-all the vacancy form offers and 2 of the 4 vacancies with applicants
 * use. Every candidate who ticked anything at all overlaps it, so counting it as
 * a matched area made 25 of 27 applicants look like a match and told the reader
 * nothing. It is excluded from the overlap and reported on its own instead.
 */
export const CATCH_ALL_AREA = "otro";

/** What a vacancy asks for, read once and compared against every applicant. */
export interface VacancyAsk {
    /** Areas the vacancy names, canonical keys, minus the catch-all. */
    areas: string[];
    /** The level it asks for in each area, where it says one. */
    levels: Record<string, AreaLevel>;
    /** It ticked "Otra área" — alone, that is nothing to compare against. */
    usesCatchAll: boolean;
    /** The four canonical credential lists, kept apart. */
    requirements: VacancyRequirements;
    /** Required certifications and skills, pooled: what a reader scans as "what it needs". */
    required: string[];
    /** Desirable ones, pooled, minus anything already required. */
    desired: string[];
}

function strings(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

export function vacancyAsk(job: Record<string, unknown> | null | undefined): VacancyAsk {
    const values = job ?? {};
    const focus = strings(values.sustainability_focus).map(canonicalAreaKey);
    const areas = [...new Set(focus.filter(key => key !== CATCH_ALL_AREA))];

    const rawLevels = values.sustainability_focus_levels;
    const levels: Record<string, AreaLevel> = {};
    if (rawLevels && typeof rawLevels === "object" && !Array.isArray(rawLevels)) {
        for (const [key, level] of Object.entries(rawLevels as Record<string, unknown>)) {
            if (isAreaLevel(level)) levels[canonicalAreaKey(key)] = level;
        }
    }

    const requirements = vacancyRequirements(values);
    return {
        areas,
        levels,
        usesCatchAll: focus.includes(CATCH_ALL_AREA),
        requirements,
        required: [...new Set([...requirements.requiredCertifications, ...requirements.requiredSkills])],
        desired: [...new Set([...requirements.desiredCertifications, ...requirements.desiredSkills])]
    };
}

/** A vacancy area the applicant claims. */
export interface AreaMatch extends TalentArea {
    /** The level the vacancy asks for here, or null when it names the area without one. */
    askedLevel: AreaLevel | null;
    askedLevelLabel: string | null;
    /** Claimed, below the level asked. Said beside the two levels; never a penalty. */
    belowAsked: boolean;
}

/** A vacancy area the applicant does not claim — or was never asked about. */
export interface AreaGap {
    key: string;
    label: string;
    askedLevel: AreaLevel | null;
    askedLevelLabel: string | null;
}

export interface ApplicantFit {
    /** Vacancy areas they claim, strongest first. */
    matched: AreaMatch[];
    /**
     * Vacancy areas they do not claim. Read against {@link answeredAreas}: "no
     * la declara" if they answered the question, "sin datos" if they never did.
     */
    missing: AreaGap[];
    /** Whether they ever answered the areas question at all. */
    answeredAreas: boolean;
    /** Of the vacancy's areas, how many they claim at `advanced`. */
    depth: number;
    /** The vacancy's required credentials against theirs. `missing` is the only hard verdict. */
    required: RequirementVerdict;
    /** The vacancy's desirable credentials against theirs. */
    desired: RequirementVerdict;
    /** Desirable credentials they hold or we read out of their profile. Never disqualifying. */
    heldDesired: string[];
    /** Advanced across half the vocabulary: read their claims more sceptically. */
    inflated: boolean;
    /** Lower bound of their declared experience, for ordering only. */
    experienceFloor: number | null;
}

/** Whether a vacancy requirement is unmet by something they were asked. The one gate. */
export function fallsShort(fit: ApplicantFit): boolean {
    return fit.required.missing.length > 0;
}

export function applicantFit(ask: VacancyAsk, talent: Record<string, unknown> | null | undefined): ApplicantFit {
    const values = talent ?? {};
    const { areas, answered } = readAreas(values.questionnaire);
    const byKey = new Map(areas.map(a => [a.key, a]));

    const asked = (key: string) => {
        const level = ask.levels[key] ?? null;
        return { askedLevel: level, askedLevelLabel: level ? LEVEL_LABELS[level] : null };
    };

    const matched: AreaMatch[] = ask.areas
        .map(key => byKey.get(key))
        .filter((a): a is TalentArea => Boolean(a))
        .map(a => {
            const { askedLevel, askedLevelLabel } = asked(a.key);
            return {
                ...a,
                askedLevel,
                askedLevelLabel,
                belowAsked: askedLevel !== null && a.rank < LEVEL_RANK[askedLevel]
            };
        })
        .sort((a, b) => b.rank - a.rank);

    const missing: AreaGap[] = ask.areas
        .filter(key => !byKey.has(key))
        .map(key => ({ key, label: AREA_LABELS[key] ?? key, ...asked(key) }));

    // What they claim, and — separately — which of the two credential
    // questions they were ever put. A candidate who predates the skills
    // question has no answer to it, which is not a no.
    const credentials = candidateCredentials(values);
    const required = classifyRequirements(
        { certifications: ask.requirements.requiredCertifications, skills: ask.requirements.requiredSkills },
        credentials
    );
    const desired = classifyRequirements(
        { certifications: ask.requirements.desiredCertifications, skills: ask.requirements.desiredSkills },
        credentials
    );

    return {
        matched,
        missing,
        answeredAreas: answered,
        depth: matched.filter(a => a.level === "advanced").length,
        required,
        desired,
        heldDesired: [...desired.held, ...desired.inferred],
        inflated: isInflated(areas),
        experienceFloor: relevantExperienceFloor(values.questionnaire)
    };
}

/** What {@link compareFit} needs to know about one applicant besides the fit. */
export interface FitOrdered {
    /** A decision is already taken — hired or rejected. */
    settled: boolean;
    fit: ApplicantFit;
    /** When they applied, ISO. The last tie-break. */
    appliedAt: string | null;
}

/**
 * Undecided first, then who meets what the vacancy requires, then desirable
 * credentials, then depth in the vacancy's areas, then declared experience,
 * then who applied first.
 *
 * Each rule only speaks when the ones before it tie — which is what makes this
 * an order and not a score. The chain, in words, for a screen to print:
 * {@link FIT_ORDER_RULES}.
 *
 * - An unmet requirement sinks, ahead of every other signal. Deliberately a
 *   gate and not a term in a score: the vacancy said the credential was
 *   necessary, so depth in its areas cannot compensate for not having it.
 *   Nobody is hidden — the applicant is still listed, with the credential
 *   named. A requirement *never asked* does not fire it: a gate that trips on a
 *   question the candidate was never put ranks the whole list by which version
 *   of the form they happened to fill.
 * - An inflated profile — advanced across half the vocabulary — sinks below an
 *   equally deep one that is not, because rewarding breadth puts the least
 *   credible self-assessments on top.
 */
export function compareFit(a: FitOrdered, b: FitOrdered): number {
    if (a.settled !== b.settled) return a.settled ? 1 : -1;
    const aShort = fallsShort(a.fit);
    const bShort = fallsShort(b.fit);
    if (aShort !== bShort) return aShort ? 1 : -1;
    if (a.fit.heldDesired.length !== b.fit.heldDesired.length) return b.fit.heldDesired.length - a.fit.heldDesired.length;
    if (a.fit.inflated !== b.fit.inflated) return a.fit.inflated ? 1 : -1;
    if (a.fit.depth !== b.fit.depth) return b.fit.depth - a.fit.depth;
    if (a.fit.matched.length !== b.fit.matched.length) return b.fit.matched.length - a.fit.matched.length;
    const ax = a.fit.experienceFloor ?? -1;
    const bx = b.fit.experienceFloor ?? -1;
    if (ax !== bx) return bx - ax;
    return (a.appliedAt ?? "").localeCompare(b.appliedAt ?? "");
}

/** {@link compareFit}, in the order it applies, for the screens that print it. */
export const FIT_ORDER_RULES: readonly string[] = [
    "Lo que está sin decidir, antes de lo ya contratado o descartado.",
    "Quien cumple todo lo que la vacante exige, antes de quien no.",
    "Quien tiene más certificaciones de las deseables.",
    "Quien no se declara avanzado en casi todo (una respuesta así vale menos).",
    "Quien declara nivel avanzado en más áreas de la vacante.",
    "Más años de experiencia y, a igualdad de todo, quien se postuló antes."
];
